/**
 * Pre-Seed · October 2026
 * The deck in progress: it opens on the site's first page (`CoverSlide`)
 * and changes from here while April's stays as sent. Internal until it is
 * sent — `myceo` is its only password, so nobody holding an April password
 * can open it.
 */

import type { DeckEdition } from '@components/press/pitch-deck/pitch-deck.types';
import {
  CoverSlide,
  DemoSlide,
  HowWeBuildSlide,
  ProblemSlide,
  MarketOpportunitySlide,
  WhyNowSlide,
  PathToVictorySlide,
  TeamSlide,
  AppendixOverviewSlide,
  CoreHypothesisSlide,
  LessonsLearnedSlide,
  StrategyCallsSlide,
  GrowthSlide,
  ProductGrowthSlide,
  MarketDetailSlide,
  CompetitiveDimensionsSlide,
  InvisibleLaborSlide,
  ToolingGapSlide,
  TechStackGapSlide,
  CognitiveLoadSlide,
  DelegationGapSlide,
  AffluenceParadoxSlide,
  UserLoveSlide,
} from '@components/press/pitch-deck/slides';

export const PRESEED_OCT_2026: DeckEdition = {
  meta: { title: 'The Home CEO', subtitle: 'Pre-Seed', date: 'October 2026' },
  passwords: {
    myceo: '',              // internal — no watermark
  },
  label: 'Pre-Seed: The Home CEO',
  tocTitle: 'Pre-Seed Oct 2026',
  // No PDF/PPTX yet: once exported and uploaded beside April's
  // (`DECK_EXPORTS`), name them here and the table of contents offers them.
  // Export with the page in capture mode, so the animated slides are taken
  // at rest (the script shoots each slide ~1.5s after it arrives); the `#`
  // swallows the April path the script appends:
  //   --url "http://localhost:5173/press/preseed_oct_2026?capture=1#"
  slides: [
    // Only the cover stands on the site's water; every slide after it on
    // the deck's plain ground (June, 2026-09-23).
    { id: 'cover', title: 'The Home CEO', category: 'main', backdrop: 'water', component: CoverSlide },
    // The homepage's How we build and Demo sections — what we are, then it
    // at work — each laid out as one picture that arrives animated and
    // comes to rest.
    { id: 'how-we-build', title: 'How We Build', category: 'main', component: HowWeBuildSlide },
    { id: 'demo', title: 'Demo', category: 'main', component: DemoSlide },
    { id: 'problem', title: 'Problem', category: 'main', component: ProblemSlide },
    // The market in the new slides' style, the case for moms (June,
    // 2026-09-23); April keeps `MarketSlide`.
    { id: 'market', title: 'Market', category: 'main', component: MarketOpportunitySlide },
    { id: 'why-now', title: 'Why Now', category: 'main', component: WhyNowSlide },
    { id: 'path-to-victory', title: 'Path to Victory', category: 'main', component: PathToVictorySlide },
    { id: 'team', title: 'Team', category: 'main', component: TeamSlide },
    { id: 'appendix-overview', title: 'Appendix', category: 'appendix', component: AppendixOverviewSlide },
    { id: 'core-hypothesis', title: 'Core Hypothesis', category: 'appendix', component: CoreHypothesisSlide },
    { id: 'lessons-learned', title: 'Lessons Learned', category: 'appendix', component: LessonsLearnedSlide },
    { id: 'strategy-calls', title: 'June’s Hunches', category: 'appendix', component: StrategyCallsSlide },
    { id: 'growth', title: 'User Growth', category: 'appendix', component: GrowthSlide },
    { id: 'product-growth', title: 'Product Growth', category: 'appendix', component: ProductGrowthSlide },
    { id: 'market-detail', title: 'Market Detail', category: 'appendix', component: MarketDetailSlide },
    { id: 'competitive-dimensions', title: 'Competitive Dimensions', category: 'appendix', component: CompetitiveDimensionsSlide },
    { id: 'invisible-labor', title: 'Invisible Labor', category: 'appendix', component: InvisibleLaborSlide },
    { id: 'tooling-gap', title: 'Tooling Gap', category: 'appendix', component: ToolingGapSlide },
    { id: 'tech-stack-gap', title: 'Tech Stack Gap', category: 'appendix', component: TechStackGapSlide },
    { id: 'cognitive-load', title: 'Cognitive Load', category: 'appendix', component: CognitiveLoadSlide },
    { id: 'delegation-gap', title: 'Delegation Gap', category: 'appendix', component: DelegationGapSlide },
    { id: 'affluence-paradox', title: 'Affluence Paradox', category: 'appendix', component: AffluenceParadoxSlide },
    { id: 'user-love', title: 'User Love', category: 'appendix', component: UserLoveSlide },
  ],
};
